import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Edit, Trash2, ClipboardList, Calendar, Users, ArrowLeft, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/api/api';
import { formatDate } from '../../utils/dateTimeUtils';

const AssignmentList = () => {
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState([]);
  const [courses, setCourses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterCourse, setFilterCourse] = useState('all');
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    course_id: '',
    subject_id: '',
    due_date: '',
    total_marks: 100,
    allow_late_submission: false
  });

  useEffect(() => {
    fetchData();
  }, [filterCourse]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [assignmentsRes, coursesRes] = await Promise.all([
        api.get('/lms/assignments', { 
          params: { course_id: filterCourse !== 'all' ? filterCourse : undefined } 
        }),
        api.get('/lms/teacher/courses')
      ]);
      
      setAssignments(assignmentsRes.data || []);
      setCourses(coursesRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load assignments');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubjects = async (courseId) => {
    if (!courseId) {
      setSubjects([]);
      return;
    }
    try {
      const response = await api.get('/lms/teacher/subjects', { params: { course_id: courseId } });
      setSubjects(response.data || []);
    } catch (error) {
      console.error('Error fetching subjects:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.course_id || !formData.due_date) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      if (editingAssignment) {
        await api.put(`/lms/assignments/${editingAssignment.id}`, formData);
        toast.success('Assignment updated successfully');
      } else {
        await api.post('/lms/assignments', formData);
        toast.success('Assignment created successfully');
      }
      
      setIsModalOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error saving assignment:', error);
      toast.error(error.response?.data?.detail || 'Failed to save assignment');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this assignment?')) return;
    
    try {
      await api.delete(`/lms/assignments/${id}`);
      toast.success('Assignment deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Error deleting assignment:', error);
      toast.error('Failed to delete assignment');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      course_id: '',
      subject_id: '',
      due_date: '',
      total_marks: 100,
      allow_late_submission: false
    });
    setEditingAssignment(null);
  };

  const openEditModal = (item) => {
    setEditingAssignment(item);
    setFormData({
      title: item.title || '',
      description: item.description || '',
      course_id: item.course_id || '',
      subject_id: item.subject_id || '',
      due_date: item.due_date?.split('T')[0] || '',
      total_marks: item.total_marks || 100,
      allow_late_submission: item.allow_late_submission || false
    });
    if (item.course_id) fetchSubjects(item.course_id);
    setIsModalOpen(true);
  };

  const getStatusBadge = (dueDate) => {
    const now = new Date();
    const due = new Date(dueDate);
    
    if (due < now) {
      return <span className="bg-red-100 text-red-700 text-xs px-2 py-1 rounded">Closed</span>;
    } else if ((due - now) / (1000 * 60 * 60 * 24) <= 3) {
      return <span className="bg-yellow-100 text-yellow-700 text-xs px-2 py-1 rounded">Due Soon</span>;
    }
    return <span className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded">Active</span>;
  };

  return (
    <div className="space-y-6" data-testid="lms-assignment-list">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => navigate('/lms')} size="sm">
            <ArrowLeft size={18} className="mr-1" /> Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Assignments</h1>
            <p className="text-gray-600">Create and manage course assignments</p>
          </div>
        </div>
        <Button 
          onClick={() => { resetForm(); setIsModalOpen(true); }}
          className="bg-green-600 hover:bg-green-700"
          data-testid="add-assignment-btn"
        >
          <Plus size={18} className="mr-2" /> Create Assignment
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-4">
            <Select value={filterCourse} onValueChange={setFilterCourse}>
              <SelectTrigger className="w-[250px]" data-testid="filter-course">
                <SelectValue placeholder="Filter by Course" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Courses</SelectItem>
                {courses.map(course => (
                  <SelectItem key={course.id} value={course.id}>{course.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Assignment List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
        </div>
      ) : assignments.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <ClipboardList className="mx-auto text-gray-400 mb-4" size={48} />
            <p className="text-gray-500">No assignments found. Create your first assignment!</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {assignments.map((item) => (
            <Card key={item.id} className="hover:shadow-md transition-shadow" data-testid={`assignment-item-${item.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-green-100 rounded-lg">
                      <ClipboardList className="text-green-600" size={24} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800">{item.title}</h3>
                      <p className="text-sm text-gray-500 line-clamp-1">{item.description || 'No description'}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Calendar size={12} />
                          Due: {formatDate(item.due_date)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Users size={12} />
                          {item.submission_count || 0} submissions
                        </span>
                        <span className="flex items-center gap-1">
                          <CheckCircle size={12} />
                          {item.total_marks} marks
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {getStatusBadge(item.due_date)}
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => navigate(`/lms/submissions?assignment_id=${item.id}`)}
                    >
                      View Submissions
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => openEditModal(item)}>
                      <Edit size={16} />
                    </Button>
                    <Button variant="outline" size="sm" className="text-red-600" onClick={() => handleDelete(item.id)}>
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingAssignment ? 'Edit Assignment' : 'Create New Assignment'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium">Title *</label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="Assignment title"
                required
                data-testid="assignment-title-input"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium">Description *</label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Assignment instructions and details"
                rows={4}
                required
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Course *</label>
                <Select 
                  value={formData.course_id} 
                  onValueChange={(val) => { 
                    setFormData({...formData, course_id: val, subject_id: ''}); 
                    fetchSubjects(val); 
                  }}
                >
                  <SelectTrigger data-testid="course-select">
                    <SelectValue placeholder="Select Course" />
                  </SelectTrigger>
                  <SelectContent>
                    {courses.map(course => (
                      <SelectItem key={course.id} value={course.id}>{course.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Subject</label>
                <Select 
                  value={formData.subject_id || 'none'} 
                  onValueChange={(val) => setFormData({...formData, subject_id: val === 'none' ? '' : val})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Subject" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No specific subject</SelectItem>
                    {subjects.map(subj => (
                      <SelectItem key={subj.id} value={subj.id}>{subj.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Due Date *</label>
                <Input
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({...formData, due_date: e.target.value})}
                  required
                  data-testid="due-date-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Total Marks</label>
                <Input
                  type="number"
                  value={formData.total_marks}
                  onChange={(e) => setFormData({...formData, total_marks: parseInt(e.target.value) || 100})}
                  placeholder="100"
                />
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="allow_late"
                checked={formData.allow_late_submission}
                onChange={(e) => setFormData({...formData, allow_late_submission: e.target.checked})}
                className="rounded"
              />
              <label htmlFor="allow_late" className="text-sm">Allow late submissions</label>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-green-600 hover:bg-green-700" data-testid="save-assignment-btn">
                {editingAssignment ? 'Update' : 'Create'} Assignment
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AssignmentList;
